import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../../components/AppIcon';
import Button from '../../../../components/ui/Button';
import Select from '../../../../components/ui/Select';
import { assignReferee } from '@/api/TournamentManagement/refereeApi';
import { MatchRole, resolveEnum } from '@/models/tournament';
import type { MatchView, RefereeView } from '@/models/tournament';

interface ConfirmAssignmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  match: MatchView | null;
  referee: RefereeView | null;
  assignmentType: 'new' | 'reassignment';
}

const ConfirmAssignmentModal: React.FC<ConfirmAssignmentModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  match,
  referee,
  assignmentType,
}) => {
  const { t, i18n } = useTranslation('tournament');
  const [notifyParties, setNotifyParties] = useState(true);
  const [selectedRole, setSelectedRole] = useState<string>('');
  const [workPointOverride, setWorkPointOverride] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  const canConfirm = selectedRole !== '';

  const roleKeys: Record<MatchRole, string> = {
    [MatchRole.HeadReferee]: 'Head Referee',
    [MatchRole.AssistantReferee]: 'Assistant Referee',
    [MatchRole.FourthOfficial]: 'Fourth Official',
    [MatchRole.Commissioner]: 'Commissioner',
  };

  const mapMatchRole = (value: string): MatchRole => {
    const num = parseInt(value, 10);
    if (num === MatchRole.AssistantReferee) return MatchRole.AssistantReferee;
    if (num === MatchRole.FourthOfficial) return MatchRole.FourthOfficial;
    if (num === MatchRole.Commissioner) return MatchRole.Commissioner;
    return MatchRole.HeadReferee;
  };

  const handleConfirm = async () => {
    if (!canConfirm || !match || !referee) return;

    setIsSubmitting(true);
    setApiError(null);

    try {
      const result = await assignReferee({
        matchId: match.id,
        refereeProfileId: referee.id,
        role: mapMatchRole(selectedRole),
        notifyParties,
        reason: assignmentType === 'reassignment' ? 'Reassignment via assignment center' : undefined,
        workPointOverride: workPointOverride ? Number(workPointOverride) : undefined,
      });

      if (result.errors.length > 0) {
        setApiError(result.errors[0]?.description || t('Failed to assign referee'));
        return;
      }

      onConfirm();
      handleClose();
    } catch (error: any) {
      const msg = error?.response?.data?.errors?.[0]?.description
        || error?.message
        || t('An unexpected error occurred. Please try again.');
      setApiError(msg);
      console.error('Error confirming assignment:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setNotifyParties(true);
    setSelectedRole('');
    setWorkPointOverride('');
    setApiError(null);
    onClose();
  };

  if (!isOpen || !match || !referee) return null;

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString(i18n.language, { month: 'short', day: 'numeric' });
  };

  const formatTime = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' });
  };

  const certificationKeys: Record<number, string> = {
    0: 'National Level A',
    1: 'AFC Elite',
    2: 'FIFA International',
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-testid="confirm-assignment-modal">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-border px-6 py-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center">
              <Icon name="AlertTriangle" size={24} className="me-2 text-amber-600" />
              {t('Confirm Referee Assignment')}
            </h2>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors focus-visible:ring-2 focus-visible:ring-primary/20"
              disabled={isSubmitting}
              data-testid="confirm-assignment-close-btn"
            >
              <Icon name="X" size={20} />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* API Error */}
          {apiError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start">
                <Icon name="AlertCircle" size={18} className="text-red-600 me-2 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-red-800">{apiError}</p>
              </div>
            </div>
          )}

          {/* Assignment Summary */}
          <div className="bg-gradient-to-br from-primary/5 to-blue-50 border border-primary/20 rounded-lg p-4">
            <p className="text-sm font-medium text-gray-700 mb-3">{t('Assignment Summary')}</p>

            <div className="grid grid-cols-2 gap-4">
              {/* Match */}
              <div className="bg-white rounded-lg p-3">
                <div className="flex items-center mb-2">
                  <Icon name="Calendar" size={14} className="text-primary me-2" />
                  <p className="text-xs font-medium text-gray-700">{t('Match')}</p>
                </div>
                <p className="text-sm font-semibold text-gray-900 mb-1">
                  {match.team1Name} {t('vs')} {match.team2Name}
                </p>
                <p className="text-xs text-gray-600">#{match.id}</p>
                <div className="flex items-center mt-2 text-xs text-gray-600">
                  <Icon name="Clock" size={12} className="me-1" />
                  <span>
                    {formatDate(match.scheduledDateTime)} at {formatTime(match.scheduledDateTime)}
                  </span>
                </div>
              </div>

              {/* Referee */}
              <div className="bg-white rounded-lg p-3">
                <div className="flex items-center mb-2">
                  <Icon name="User" size={14} className="text-primary me-2" />
                  <p className="text-xs font-medium text-gray-700">{t('Referee')}</p>
                </div>
                <div className="flex items-center space-x-2 rtl:space-x-reverse mb-2">
                  {referee.photoUrl ? (
                    <img
                      src={referee.photoUrl}
                      alt={`${referee.name} profile photo`}
                      className="w-10 h-10 rounded-full object-cover border-2 border-border"
                    />
                  ) : (
                    <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center border-2 border-border">
                      <Icon name="User" size={18} className="text-primary" />
                    </div>
                  )}
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-gray-900">{referee.name}</p>
                    <p className="text-xs text-gray-600">
                      {t(certificationKeys[referee.certification] ?? 'Unknown')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Match Role Selection */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm font-medium text-blue-900 mb-2">{t('Select Match Role')}</p>
            <Select
              value={selectedRole}
              onChange={setSelectedRole}
              options={[
                { value: '', label: t('Select Role') },
                { value: String(MatchRole.HeadReferee), label: t('Head Referee') },
                { value: String(MatchRole.AssistantReferee), label: t('Assistant Referee') },
                { value: String(MatchRole.FourthOfficial), label: t('Fourth Official') },
                { value: String(MatchRole.Commissioner), label: t('Commissioner') },
              ]}
              placeholder={t('Select Role')}
              data-testid="confirm-assignment-role-select"
            />
            {!selectedRole && (
              <p className="text-xs text-blue-700 mt-1">{t('A match role is required to proceed.')}</p>
            )}
          </div>

          {/* Work Point Override */}
          <div className="bg-gray-50 dark:bg-gray-800 border border-border rounded-lg p-4">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('Work Point Override')}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
              {t('Leave empty to use automatic calculation based on work point rules.')}
            </p>
            <input
              type="number"
              value={workPointOverride}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setWorkPointOverride(e.target.value)}
              placeholder={t('Auto')}
              min="0"
              className="w-32 px-3 py-2 text-sm bg-background border border-input rounded-lg focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary transition-colors outline-none"
              disabled={isSubmitting}
              data-testid="confirm-assignment-workpoint-override-input"
            />
          </div>

          {/* Existing Referee Team Members */}
          {match.refereeTeamMembers?.length > 0 && (
            <div className="bg-muted/50 border border-border rounded-lg p-4">
              <p className="text-sm font-medium text-foreground mb-2">{t('Existing Referee Team Members')}</p>
              <div className="space-y-1.5">
                {match.refereeTeamMembers.map((member) => (
                  <div key={member.id} className="flex items-center text-sm">
                    <Icon name="User" size={14} className="text-muted-foreground me-2" />
                    <span className="font-medium text-foreground">{member.userName}</span>
                    <span className="text-muted-foreground ms-1.5 text-xs">
                      ({t(roleKeys[(resolveEnum(MatchRole, member.role) as MatchRole) ?? member.role] ?? 'Unknown')})
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Warnings Section */}
          <div className="space-y-3">
            {/* Reassignment Warning */}
            {assignmentType === 'reassignment' && (match.refereeTeamMembers?.length ?? 0) > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-start">
                  <Icon name="UserX" size={18} className="text-amber-600 me-3 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-bold text-amber-900 mb-2">{t('Adding to Existing Team')}</p>
                    <p className="text-sm text-amber-800 mb-2">
                      {t('{{newName}} will be added to the referee team for this match.', { newName: referee.name })}
                    </p>
                    <p className="text-sm text-amber-700">
                      {t('The referee will be notified of this assignment.')}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Notification Option */}
          <div className="bg-gray-50 border border-border rounded-lg p-4">
            <div className="flex items-start space-x-3 rtl:space-x-reverse">
              <input
                type="checkbox"
                id="notify-parties"
                checked={notifyParties}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNotifyParties(e.target.checked)}
                className="mt-1 rounded border-border text-primary focus:ring-2 focus:ring-primary"
                disabled={isSubmitting}
                data-testid="confirm-assignment-notify-checkbox"
              />
              <label htmlFor="notify-parties" className="flex-1 cursor-pointer">
                <p className="text-sm font-medium text-gray-900">{t('Send notifications')}</p>
                <p className="text-xs text-gray-600 mt-1">
                  {t('Notify referee, tournament officials, and venue management about this assignment')}
                </p>
              </label>
            </div>
          </div>

        </div>

        {/* Actions */}
        <div className="border-t border-border px-6 py-4 flex justify-end space-x-3 rtl:space-x-reverse bg-gray-50">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isSubmitting}
            data-testid="confirm-assignment-cancel-btn"
          >
            {t('Cancel')}
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!canConfirm || isSubmitting}
            loading={isSubmitting}
            iconName="CheckCircle"
            data-testid="confirm-assignment-submit-btn"
          >
            {t('Assign Referee')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmAssignmentModal;
